import { useMemo } from 'react';

import _ from 'lodash';

import { checkIsPendingChangeHasDifference, mergePendingChanges } from 'utils/pendingChanges';

import { applyCompanyNameHighlights, prefixChanges } from '../Content.utils';

export const usePendingChangesData = ({
  form,
  observableFieldsNames,
  formPendingChanges,
  remotePendingChanges,
  loanPendingChangesData,
  trustPendingChanges,
  titleCompanyPendingChanges,
  titleLocationPendingChanges,
  escrowCompanyPendingChanges,
  escrowLocationPendingChanges,
  sellerAttorneyPendingChanges,
  sellerAttorneyLocationPendingChanges,
  buyerAttorneyPendingChanges,
  buyerAttorneyLocationPendingChanges,
}) => {
  const pendingChangesData = useMemo(
    () => ({
      ...loanPendingChangesData,
      ...trustPendingChanges,
      ...prefixChanges(titleCompanyPendingChanges, 'title'),
      ...prefixChanges(titleLocationPendingChanges, 'title'),
      ...prefixChanges(escrowCompanyPendingChanges, 'escrow'),
      ...prefixChanges(escrowLocationPendingChanges, 'escrow'),
      ...prefixChanges(sellerAttorneyPendingChanges, 'sellerAttorney'),
      ...prefixChanges(sellerAttorneyLocationPendingChanges, 'sellerAttorney'),
      ...prefixChanges(buyerAttorneyPendingChanges, 'buyerAttorney'),
      ...prefixChanges(buyerAttorneyLocationPendingChanges, 'buyerAttorney'),
    }),
    [
      loanPendingChangesData,
      trustPendingChanges,
      titleCompanyPendingChanges,
      escrowCompanyPendingChanges,
      titleLocationPendingChanges,
      escrowLocationPendingChanges,
      sellerAttorneyPendingChanges,
      sellerAttorneyLocationPendingChanges,
      buyerAttorneyPendingChanges,
      buyerAttorneyLocationPendingChanges,
    ],
  );

  const filteredBackendPendingChanges = useMemo(() => {
    if (_.isNil(pendingChangesData)) {
      return {};
    }

    const filtered = {};
    const fieldNamesSet = new Set(observableFieldsNames);

    Object.entries(pendingChangesData).forEach(([fieldName, pendingChange]) => {
      if (fieldNamesSet.has(fieldName)) {
        filtered[fieldName] = pendingChange;
      }
    });

    return filtered;
  }, [pendingChangesData, observableFieldsNames]);

  const filteredFormPendingChanges = useMemo(() => {
    if (_.isNil(formPendingChanges)) {
      return {};
    }

    const filtered = {};

    Object.entries(formPendingChanges).forEach(([fieldName, pendingChange]) => {
      if (checkIsPendingChangeHasDifference(pendingChange)) {
        filtered[fieldName] = pendingChange;
      }
    });

    return filtered;
  }, [formPendingChanges]);

  const mergedPendingChanges = useMemo(() => {
    const merged = mergePendingChanges(filteredBackendPendingChanges, filteredFormPendingChanges);

    applyCompanyNameHighlights({
      merged,
      locationPendingChanges: [
        { changes: titleLocationPendingChanges, formKey: 'titleAttendantCompanyId' },
        { changes: escrowLocationPendingChanges, formKey: 'escrowAttendantCompanyId' },
        {
          changes: sellerAttorneyLocationPendingChanges,
          formKey: 'sellerAttorneyAttendantCompanyId',
        },
        {
          changes: buyerAttorneyLocationPendingChanges,
          formKey: 'buyerAttorneyAttendantCompanyId',
        },
      ],
      formPendingChanges: filteredFormPendingChanges,
      form,
    });

    Object.entries(remotePendingChanges).forEach(([fieldName, pendingChange]) => {
      merged[fieldName] = pendingChange;
    });

    return merged;
  }, [
    filteredBackendPendingChanges,
    filteredFormPendingChanges,
    remotePendingChanges,
    titleLocationPendingChanges,
    escrowLocationPendingChanges,
    sellerAttorneyLocationPendingChanges,
    buyerAttorneyLocationPendingChanges,
    form,
  ]);

  return { mergedPendingChanges };
};
